// Les parcours : les golfs où l'on va jouer.

import { api } from './api.ts';

export interface Course {
    id: string;
    name: string;
    city: string | null;
    country: string | null;
    holeCount: number;
    latitude: number | null;
    longitude: number | null;
    photoPath: string | null;
    createdBy: string | null;
    /** Prénom seul : de quoi savoir à qui l'on doit la fiche. */
    createdByFirstName: string | null;
    /** Ce que le joueur a fait ici. `myRounds` à 0 : jamais joué. */
    myRounds: number;
    myLastPlayedAt: string | null;
    myBestStrokes: number | null;
    playedByOthers: boolean;
    /** `null` quand la position est inconnue ou le parcours non situé. */
    distanceKm: number | null;
    /** Calculés par le serveur, qui applique les mêmes règles à l'écriture. */
    canEdit: boolean;
    canDelete: boolean;
}

export interface CoursePage {
    courses: Course[];
    hasMore: boolean;
    nextOffset: number;
}

export interface CourseDraft {
    name: string;
    city?: string | null;
    country?: string | null;
    latitude?: number | null;
    longitude?: number | null;
    holeCount?: number;
    notes?: string | null;
}

export function listCourses(params: {
    search?: string; lat?: number | null; lon?: number | null; offset?: number;
}): Promise<CoursePage> {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.lat != null && params.lon != null) {
        q.set('lat', String(params.lat));
        q.set('lon', String(params.lon));
    }
    if (params.offset) q.set('offset', String(params.offset));
    return api<CoursePage>(`/courses?${q.toString()}`);
}

/** Un parcours seul, pour l'ecran de correction.
 *
 *  Une route dediee plutot qu'une recherche dans la liste : celle-ci est
 *  paginee, et un parcours au-dela de la premiere tranche n'y figure pas. */
export const getCourse = (id: string) =>
    api<Course & { canEdit: boolean }>(`/courses/${id}`);

export const createCourse = (body: CourseDraft) =>
    api<{ id: string }>('/courses', { method: 'POST', body });

export const updateCourse = (id: string, body: Partial<CourseDraft>) =>
    api<void>(`/courses/${id}`, { method: 'PATCH', body });

export const deleteCourse = (id: string) =>
    api<void>(`/courses/${id}`, { method: 'DELETE' });

/** La position du joueur, ou `null` s'il refuse ou si l'appareil ne sait pas.
 *
 *  Jamais bloquant : la liste s'affiche dans tous les cas, par ordre
 *  alphabétique à défaut. Un écran qui attend une autorisation refusée est un
 *  écran vide, et le joueur ne saura pas pourquoi.
 *
 *  Le délai est court — six secondes. Sur un parking de golf, une localisation
 *  qui met trente secondes arrive après que le joueur a déjà trouvé son golf
 *  à la main. */
export interface Position { lat: number; lon: number }

/* La position, demandée une fois et gardée.
 *
 * ── Pourquoi une réserve en mémoire ────────────────────────────────────────
 *
 * Un relevé GPS prend deux à six secondes sur un téléphone, et la liste des
 * parcours l'attend pour se trier. La refaire à chaque ouverture de l'écran,
 * c'est cette attente à chaque fois — alors qu'un golf ne bouge pas et que le
 * joueur non plus, à l'échelle du kilomètre qui sépare deux terrains.
 *
 * ── Pourquoi on n'interroge PAS au lancement si la permission n'est pas
 *    déjà accordée ───────────────────────────────────────────────────────────
 *
 * Demander la position dès l'ouverture ferait surgir la fenêtre du navigateur
 * avant que le joueur ait rien fait, sans qu'il puisse deviner à quoi elle
 * sert. Refusée là, elle est refusée pour de bon.
 *
 * Le relevé se déclenche donc au lancement UNIQUEMENT si la permission est
 * déjà accordée. Sinon on attend l'écran des parcours, où la question
 * s'explique d'elle-même — et la réponse sert toutes les fois suivantes.
 */
let reserve: { valeur: Position | null; a: number } | null = null;

/** Au-delà, la réserve est périmée. Dix minutes : on peut avoir roulé jusqu'au
 *  club entre-temps, mais pas pendant qu'on regarde la liste. */
const FRAICHEUR_MS = 10 * 60_000;

function releve(): Promise<Position | null> {
    if (!('geolocation' in navigator)) return Promise.resolve(null);
    return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
            (p) => resolve({ lat: p.coords.latitude, lon: p.coords.longitude }),
            () => resolve(null),
            { timeout: 6000, maximumAge: FRAICHEUR_MS },
        );
    });
}

export async function currentPosition(): Promise<Position | null> {
    if (reserve && Date.now() - reserve.a < FRAICHEUR_MS) return reserve.valeur;
    const valeur = await releve();
    reserve = { valeur, a: Date.now() };
    return valeur;
}

/** Prend l'avance au lancement, sans jamais provoquer la demande de permission.
 *
 *  `navigator.permissions` manque encore sur quelques navigateurs : son absence
 *  fait simplement renoncer, elle ne doit pas déclencher la demande par
 *  défaut. */
export function amorcerPosition(): void {
    if (!('geolocation' in navigator) || !navigator.permissions?.query) return;
    void navigator.permissions.query({ name: 'geolocation' })
        .then((etat) => { if (etat.state === 'granted') void currentPosition(); })
        .catch(() => { /* pas de permissions API : on attendra l'écran */ });
}

// ── Le carnet de parcours ───────────────────────────────────────────────────

export interface CardTee {
    id: string;
    label: string;
    sortOrder: number;
    /* Ce que ce départ dit de la difficulté, et qui décide des coups reçus.
     *
     * DEUX jeux de valeurs : c'est ainsi qu'une carte française les publie, et
     * deux partenaires sur le même repère ne reçoivent pas le même nombre de
     * coups. Facultatifs : sans eux le calcul retombe sur le cas neutre. */
    slopeHommes: number | null;
    slopeFemmes: number | null;
    courseRatingHommes: number | null;
    courseRatingFemmes: number | null;
}

export interface CardHole {
    number: number;
    par: number | null;
    strokeIndex: number | null;
    /** Distances en mètres, par libellé de départ. */
    distances: Record<string, number | null>;
}

export interface CourseCardData {
    course: {
        id: string; name: string; holeCount: number; createdBy: string | null;
        /** Le plafond du handicap de jeu imposé par CE parcours, s'il en impose
         *  un. `null` laisse valoir le réglage du joueur. */
        maxHandicapStrokes: number | null;
    };
    tees: CardTee[];
    holes: CardHole[];
    canEdit: boolean;
}

/** Les départs français usuels, du plus long au plus court.
 *
 *  Proposés, jamais imposés : les couleurs varient d'un pays à l'autre, et le
 *  champ libre reste là pour les golfs qui sortent du cadre. */
/* Les repères usuels, du plus long au plus court.
 *
 * Les ORANGE ferment la liste : ce sont les départs les plus avancés, ceux des
 * jeunes et des parcours famille. Ils manquaient, et un parcours qui les
 * propose obligeait à les saisir à la main. */
export const DEFAULT_TEES = ['Noirs', 'Blancs', 'Jaunes', 'Bleus', 'Rouges', 'Orange'];

export const getCourseCard = (id: string) =>
    api<CourseCardData>(`/courses/${id}/card`);

export const saveCourseCard = (id: string, body: {
    maxHandicapStrokes?: number | null;
    tees: {
        label: string;
        slopeHommes?: number | null;
        slopeFemmes?: number | null;
        courseRatingHommes?: number | null;
        courseRatingFemmes?: number | null;
    }[];
    holes: { number: number; par: number | null; strokeIndex: number | null;
             distances: Record<string, number | null> }[];
}) => api<void>(`/courses/${id}/card`, { method: 'PUT', body });

// -- Export / import --------------------------------------------------------

/** Le numero de format du document d'echange. Le serveur refuse tout autre. */
export const FORMAT_PARCOURS = 'golf.parcours/1';

export interface CourseDocument {
    format: string;
    exportedAt?: string;
    course: CourseDraft & { holeCount: number };
    tees: string[];
    holes: CardHole[];
}

/** Le parcours entier, sans identifiants ni auteur : ceux-ci n'ont de sens
 *  que dans la base qui les a emis. */
export const exportCourse = (id: string): Promise<CourseDocument> =>
    api<CourseDocument>(`/courses/${id}/export`);

/** Ecrit le parcours : il ecrase celui du meme nom dans la meme ville, ou le
 *  cree s'il n'y est pas. `created` dit lequel des deux a eu lieu — c'est la
 *  seule chose que l'ecran ait besoin de rapporter. */
export const importCourse = (doc: CourseDocument): Promise<{ id: string; created: boolean }> =>
    api<{ id: string; created: boolean }>('/courses/import', { method: 'POST', body: doc });

/** Le nom de fichier d'un parcours exporte.
 *
 *  Tire du nom du golf : dix exports dans un dossier de telechargements se
 *  distinguent alors sans les ouvrir. */
export function nomDeFichier(nom: string): string {
    const propre = nom.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^\w]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
    return `${propre || 'parcours'}.json`;
}

/** Remet un document entre les mains du joueur, sous forme de fichier.
 *
 *  Le fichier plutot que le presse-papier : c'est ce qui se garde, se relit,
 *  se corrige a la main et se reimporte. Un presse-papier s'ecrase au premier
 *  copier suivant. */
export function telechargerDocument(doc: CourseDocument, nom: string): void {
    const url = URL.createObjectURL(
        new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = nomDeFichier(nom);
    a.click();
    URL.revokeObjectURL(url);
}

/** Relit un document colle ou charge depuis un fichier.
 *
 *  La verification est faite ICI et pas seulement au serveur : coller le
 *  mauvais presse-papier est l'erreur la plus frequente, et un message clair
 *  vaut mieux qu'un aller-retour reseau pour l'apprendre. */
export function lireDocument(texte: string): CourseDocument {
    let brut: unknown;
    try {
        brut = JSON.parse(texte);
    } catch {
        throw new Error("Ce n'est pas un document de parcours : le texte n'est pas du JSON.");
    }
    if (!brut || typeof brut !== 'object') throw new Error('Document vide.');
    const d = brut as Partial<CourseDocument>;
    if (d.format !== FORMAT_PARCOURS) {
        throw new Error(
            d.format
                ? `Format « ${String(d.format)} » inconnu de cette version.`
                : "Ce document n'est pas un parcours exporte.",
        );
    }
    if (!d.course?.name) throw new Error('Le document ne nomme aucun parcours.');
    return d as CourseDocument;
}
